'use client';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { useLanguage } from '@/components/language-provider';
import { 
  Cpu, 
  Zap, 
  Shield, 
  Wrench,
  ArrowRight,
  Play,
  Download,
  CheckCircle
} from 'lucide-react';

export default function ProductsPage() {
  const { t } = useLanguage();

  const productCategories = [
    {
      id: 'industrial',
      name: 'Industrial Series',
      description: 'Heavy-duty solutions for industrial automation',
      products: [
        {
          name: 'RS-1000',
          subtitle: 'Heavy-Duty Industrial Sole',
          description: 'Designed for high-load industrial robots with superior durability and stability.',
          specs: ['Load Capacity: 500kg', 'Service Life: 50,000+ hours', 'Materials: High-grade steel alloy', 'Surface: Anti-slip coating'],
          features: ['High load bearing', 'Extended durability', 'Temperature resistant', 'Easy maintenance'],
        },
        {
          name: 'RS-2000',
          subtitle: 'Precision Movement Sole',
          description: 'Optimized for precision movements and smooth operation in manufacturing environments.',
          specs: ['Load Capacity: 300kg', 'Service Life: 40,000+ hours', 'Materials: Carbon fiber composite', 'Surface: Low-friction coating'],
          features: ['Precision control', 'Smooth operation', 'Lightweight design', 'Low noise'],
        },
      ],
    },
    {
      id: 'service',
      name: 'Service Robot Series',
      description: 'Solutions for service and collaborative robots',
      products: [
        {
          name: 'RS-S100',
          subtitle: 'Collaborative Robot Sole',
          description: 'Safe and flexible solution for human-robot collaboration environments.',
          specs: ['Load Capacity: 100kg', 'Service Life: 30,000+ hours', 'Materials: Soft polymer blend', 'Surface: Impact-absorbing'],
          features: ['Human-safe design', 'Impact absorption', 'Flexible movement', 'Lightweight'],
        },
        {
          name: 'RS-S200',
          subtitle: 'Service Robot Sole',
          description: 'Ideal for service robots in retail, hospitality, and healthcare settings.',
          specs: ['Load Capacity: 80kg', 'Service Life: 25,000+ hours', 'Materials: Eco-friendly materials', 'Surface: Quiet operation'],
          features: ['Whisper quiet', 'Floor-friendly', 'Aesthetic design', 'Easy cleaning'],
        },
      ],
    },
    {
      id: 'special',
      name: 'Specialized Series',
      description: 'Custom solutions for specific applications',
      products: [
        {
          name: 'RS-M100',
          subtitle: 'Medical Robot Sole',
          description: 'Medical-grade solution for surgical and rehabilitation robots.',
          specs: ['Load Capacity: 150kg', 'Service Life: 20,000+ hours', 'Materials: Medical-grade materials', 'Surface: Sterilizable'],
          features: ['Medical certified', 'Sterilizable', 'High precision', 'Smooth movement'],
        },
        {
          name: 'RS-A100',
          subtitle: 'Agricultural Robot Sole',
          description: 'Rugged solution for agricultural and outdoor applications.',
          specs: ['Load Capacity: 400kg', 'Service Life: 35,000+ hours', 'Materials: Weather-resistant', 'Surface: All-terrain grip'],
          features: ['All-terrain capable', 'Weather resistant', 'Self-cleaning', 'Long service life'],
        },
      ],
    },
  ];

  const technologies = [
    {
      title: 'Advanced Materials',
      description: 'We use cutting-edge materials including carbon fiber composites, high-grade steel alloys, and specialized polymers for optimal performance.',
      icon: Shield,
    },
    {
      title: 'Intelligent Design',
      description: 'Our products feature intelligent shock absorption systems and adaptive grip technology for superior performance.',
      icon: Cpu,
    },
    {
      title: 'Precision Engineering',
      description: 'Every product undergoes rigorous quality control and testing to ensure the highest standards.',
      icon: Wrench,
    },
    {
      title: 'Energy Efficiency',
      description: 'Optimized designs reduce energy consumption and extend operational time.',
      icon: Zap,
    },
  ];

  return (
    <div className="flex flex-col">
      {/* Hero Section */}
      <section className="relative py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <h1 className="text-4xl md:text-5xl font-bold">{t('products.title')}</h1>
            <p className="text-lg text-muted-foreground">
              {t('products.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Product Categories Tabs */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <Tabs defaultValue="industrial" className="w-full">
            <TabsList className="grid w-full max-w-3xl mx-auto grid-cols-3 mb-12">
              {productCategories.map((category) => (
                <TabsTrigger key={category.id} value={category.id}>
                  {category.name}
                </TabsTrigger>
              ))}
            </TabsList>

            {productCategories.map((category) => (
              <TabsContent key={category.id} value={category.id}>
                <div className="mb-8">
                  <h2 className="text-2xl font-bold mb-2">{category.name}</h2>
                  <p className="text-muted-foreground">{category.description}</p>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  {category.products.map((product, index) => (
                    <Card key={index} className="border-border">
                      <CardHeader>
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <CardTitle className="text-2xl">{product.name}</CardTitle>
                            <CardDescription className="text-base mt-1">
                              {product.subtitle}
                            </CardDescription>
                          </div>
                          <Badge>Featured</Badge>
                        </div>
                        <p className="text-sm text-muted-foreground mt-2">
                          {product.description}
                        </p>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div>
                          <h4 className="font-semibold mb-2">Specifications</h4>
                          <ul className="space-y-1">
                            {product.specs.map((spec, i) => (
                              <li key={i} className="text-sm text-muted-foreground flex items-center gap-2">
                                <CheckCircle className="h-4 w-4 text-primary" />
                                {spec}
                              </li>
                            ))}
                          </ul>
                        </div>

                        <div>
                          <h4 className="font-semibold mb-2">Key Features</h4>
                          <div className="flex flex-wrap gap-2">
                            {product.features.map((feature, i) => (
                              <Badge key={i} variant="secondary">
                                {feature}
                              </Badge>
                            ))}
                          </div>
                        </div>

                        <div className="flex gap-3 pt-4">
                          <Button className="flex-1">
                            Request Quote
                            <ArrowRight className="ml-2 h-4 w-4" />
                          </Button>
                          <Button variant="outline" size="icon">
                            <Download className="h-4 w-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>

      {/* Technology Section */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Our Technology
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Advanced technology and innovation at the core of every product.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {technologies.map((tech, index) => (
              <Card key={index} className="border-border">
                <CardHeader>
                  <tech.icon className="h-10 w-10 text-primary mb-2" />
                  <CardTitle>{tech.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-base">
                    {tech.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Video Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Product Demonstration
              </h2>
              <p className="text-lg text-muted-foreground">
                See our products in action
              </p>
            </div>

            <div className="aspect-video bg-muted rounded-lg overflow-hidden border border-border shadow-lg relative">
              <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-primary/10 to-primary/5">
                <Button size="lg" variant="secondary" className="rounded-full h-20 w-20">
                  <Play className="h-8 w-8" />
                </Button>
              </div>
              <div className="absolute bottom-4 left-4 text-sm text-muted-foreground">
                Product Demo Video
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Application Scenarios */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Application Scenarios
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Our products are designed for a wide range of robotics applications.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                title: 'Manufacturing',
                description: 'Assembly line robots, welding robots, material handling',
                applications: ['Automotive', 'Electronics', 'Metal processing'],
              },
              {
                title: 'Logistics',
                description: 'Warehouse automation, sorting systems, delivery robots',
                applications: ['E-commerce', 'Warehousing', 'Distribution centers'],
              },
              {
                title: 'Healthcare',
                description: 'Surgical robots, rehabilitation systems, hospital automation',
                applications: ['Surgery', 'Rehabilitation', 'Patient care'],
              },
              {
                title: 'Agriculture',
                description: 'Harvesting robots, planting systems, field monitoring',
                applications: ['Farming', 'Greenhouses', 'Orchards'],
              },
              {
                title: 'Service Industry',
                description: 'Reception robots, cleaning robots, delivery robots',
                applications: ['Hotels', 'Restaurants', 'Retail stores'],
              },
              {
                title: 'Research & Education',
                description: 'Educational robots, research platforms, training systems',
                applications: ['Universities', 'Research labs', 'Training centers'],
              },
            ].map((scenario, index) => (
              <Card key={index} className="border-border">
                <CardHeader>
                  <CardTitle>{scenario.title}</CardTitle>
                  <CardDescription>{scenario.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {scenario.applications.map((app, i) => (
                      <Badge key={i} variant="secondary">
                        {app}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <h2 className="text-3xl md:text-4xl font-bold">
              Need a Custom Solution?
            </h2>
            <p className="text-lg text-muted-foreground">
              Our engineering team can develop custom robotics footwear solutions tailored to your specific requirements.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
              <Button size="lg" asChild>
                <a href="/contact">
                  Contact Our Team
                  <ArrowRight className="ml-2 h-5 w-5" />
                </a>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <a href="/solutions">
                  View Solutions
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
